package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ComponentAst

/**
 * 组件注册表（ADR-0038）。
 *
 * 注册键为**相对路径**（如 `loading/Spinner`），使同名文件可在不同目录共存；
 * 另维护「裸名（末段）→ 候选路径 id 列表」反向索引，供调用点按裸名解析：
 * 唯一即定位、多处即歧义（由调用方报 E326，提示用 import 别名消歧）。
 * 内联组件 id 无目录段，其裸名即 id，与路径组件在同一索引中共存。
 *
 * 不做线程安全保证；编译期单线程使用。
 */
class ComponentRegistry {

    /** 路径 id → ComponentAst。 */
    private val byPath = LinkedHashMap<String, ComponentAst>()

    /** 裸名（路径末段 / 内联裸 id）→ 路径 id 列表（注册顺序）。 */
    private val byBareName = LinkedHashMap<String, MutableList<String>>()

    /** 是否存在指定**路径 id**的组件（重名诊断按路径，见 [ComponentCollectorPass]）。 */
    fun contains(name: String): Boolean = name in byPath

    /** 直接注册（重名由 [ComponentCollectorPass] 负责诊断，不在此处校验）。 */
    internal fun put(name: String, ast: ComponentAst) {
        byPath[name] = ast
        val bare = name.substringAfterLast('/')
        byBareName.getOrPut(bare) { mutableListOf() }.add(name)
    }

    /** 按**路径 id** 精确查找（import 别名解析用）。 */
    fun find(name: String): ComponentAst? = byPath[name]

    /** 按裸名（末段）返回候选路径 id 列表：0=未找到 / 1=唯一 / ≥2=歧义。 */
    fun findIdsByBareName(bare: String): List<String> = byBareName[bare] ?: emptyList()

    /** 裸名或精确路径是否可定位到至少一个组件（不判歧义，供 overlay 引用校验）。 */
    fun resolvable(name: String): Boolean = name in byBareName || name in byPath

    /** 返回稳定有序的组件视图（注册顺序，按路径 id）。 */
    fun all(): Map<String, ComponentAst> = byPath.toMap()
}
